// src/utils/configFormatters.ts
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import utc from 'dayjs/plugin/utc'; 

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);


const parseUtcOffsetMinutes = (tzText?: string): number => {
  // "IST (UTC+5:30)", "CEST (UTC+2)" -> minutes
  if (!tzText) return 0;
  const m = tzText.match(/UTC([+-]\d{1,2})(?::(\d{2}))?/i);
  if (!m) return 0;
  const hours = parseInt(m[1], 10);
  const mins = m[2] ? parseInt(m[2], 10) : 0;
  return hours * 60 + (hours >= 0 ? mins : -mins);
};

export const formatDate = (date: string | Date, config?: any): string => {
  const rawFormat = config?.date_format || 'DD/MM/YYYY';
  const separator = config?.date_separator || '/';
  const formatWithSeparator = rawFormat.replace(/[-./]/g, separator);
  return dayjs(date).format(formatWithSeparator);
};

export const formatCurrency = (value: number | string, config?: any): string => {
  const symbol = config?.currency_symbol || '₹';
  const decimals = config?.currency_decimal_places ?? 2;

  const numericValue = Number(value);

  if (isNaN(numericValue)) return `${symbol} 0.00`;

  return `${symbol} ${numericValue.toFixed(decimals)}`;
};

// Format number with grouping (e.g., 9,99,999.99 or 1,000,000.00)
export const formatNumber = (value: number, config?: any): string => {
  const decimals = config?.currency_decimal_places ?? 2;
  const format = config?.number_format || '1,000,000.00';

  const isIndianFormat = format.startsWith('9,99');

  return value.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...(isIndianFormat && { useGrouping: true }),
  });
};

// Format time (12 Hours / 24 Hours)
export const formatTime = (date: string | Date, config?: any): string => {
  const timeFormat = config?.time_format === '12 Hours' ? 'hh:mm A' : 'HH:mm';
  return dayjs(date).format(timeFormat);
};

// Format full date-time
export const formatFullDateTime = (date: string | Date, config?: any): string => {
  const formattedDate = formatDate(date, config);
  const formattedTime = formatTime(date, config);
  return `${formattedDate} ${formattedTime}`;
};



export const formatPlainTime = (time: string | null | undefined, config?: any): string => {
  if (!time) return '';
  const normalized =
    /^\d{2}:\d{2}:\d{2}$/.test(time)
      ? `1970-01-01T${time}`
      : /^\d{2}:\d{2}$/.test(time)
      ? `1970-01-01T${time}:00`
      : time;

  const fmt = config?.time_format === '12 Hours' ? 'hh:mm A' : 'HH:mm';
  const d = dayjs(normalized, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm'], true);

  return d.isValid() ? d.format(fmt) : '';
};

/**
 * Keep your existing helpers...
 * (formatDate, formatCurrency, formatNumber, formatTime, formatFullDateTime)
 * 
 * If you still want a timezone-aware formatter for full date-times:
 */
export const formatTimeByZone = (
  value: string | Date,
  config?: any,
  sourceTzText?: string
): string => {
  if (!value) return '';

  const timeFormat = config?.time_format === '12 Hours' ? 'hh:mm A' : 'HH:mm';
  const targetTzText = config?.timezone || 'UTC'; // ✅ target must be config tz
  const targetOffsetMin = parseUtcOffsetMinutes(targetTzText);

  const str = typeof value === 'string' ? value.trim() : '';
  const isTimeOnly = /^\d{2}:\d{2}(:\d{2})?$/.test(str);

  if (isTimeOnly) {
    // --- Time-only path: pure math, no utc() ---
    const normalized = str.length === 5 ? `${str}:00` : str; // HH:mm:ss
    const [h, m, s] = normalized.split(':').map(Number);

    const sourceOffsetMin = parseUtcOffsetMinutes(sourceTzText);
    const minutesLocal = h * 60 + m;                // minutes in source local clock
    const minutesUtc = minutesLocal - sourceOffsetMin;
    let minutesTarget = minutesUtc + targetOffsetMin;
    minutesTarget = ((minutesTarget % 1440) + 1440) % 1440; // wrap into 0..1439

    const base = dayjs('1970-01-01 00:00:00', 'YYYY-MM-DD HH:mm:ss', true);
    return base.add(minutesTarget, 'minute').second(s || 0).format(timeFormat);
  }

  // --- Full datetime path ---
  const d = dayjs(value);
  if (!d.isValid()) return '';
  const asUtc = dayjs.utc(d.toDate());
  return asUtc.add(targetOffsetMin, 'minute').format(timeFormat);
};